import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { RouteComponentProps } from "react-router-dom";
import URI from "urijs";
import { FormattedMessage } from "react-intl-phraseapp";
import { SkipNav } from "@cmsgov/ds-medicare-gov";
import {
  LowIncomeSubsidyStatus,
  ActionType,
  PlanType,
  PlanTypeLabels,
  SearchResultPlan,
} from "../../@types";
import { AppStore, defaultState } from "../../app/store";
import {
  fetchCurrentPlan,
  doFiltersExist,
  isCurrentPlan,
  isMedicareAdvantageType,
  useTranslate,
  useYearInTitle,
  parseSearchParams,
  getOppositePlanType,
} from "../../helpers";
import CoveragePlanCard from "./CoveragePlanCard";
import OriginalMedicareHelpDrawer from "./OriginalMedicareHelpDrawer";
import OriginalMedicarePlanCard from "./OriginalMedicarePlanCard";
import Pagination from "../../components/Pagination";
import PlanCompareFooter from "./PlanCompareFooter";
import SearchResultsPageHeader from "./SearchResultsPageHeader";
import SortSelector from "./SortSelector";
import {
  AppPage,
  ActionsBar,
  FocusableHeader,
  PlanCard,
} from "../../components";
import { PlanFiltersContainer } from "./filters";
import {
  useSearchResultsHelper,
  SearchResultsState,
} from "./useSearchResultsHelper";
import routes from "../../app/routes";

/**
 * If the user has added drugs, return the url that displays the list the drugs.
 * If not, return the url that displays the manage prescription page.
 * @param hasPrescriptions indicates if the user has added drugs or not
 * @returns the "Back to drugs & pharmacies" url
 */
export function getBackToDrugsAndPharmaciesUrl(
  hasPrescriptions: boolean
): string {
  return hasPrescriptions
    ? routes.prescriptionsList
    : routes.managePrescriptions;
}

/**
 * The Search Results page displays [[PlanCard]]s for each plan returned from the API.
 *
 * This page receives a search string from [[FindPlanPage]] via react-router's `location.search` prop. This string contains a 'fips' code which is used to identify which plans are available to the user based on their geographic location.
 */
export const SearchResultsPage: FunctionComponent<RouteComponentProps> = props => {
  const { state, dispatch: appDispatch } = useContext(AppStore.AppContext);
  const [ignoreHeaderFocus, setIgnoreHeaderFocus] = useState(true);
  const [pageState, setPageState] = useState({
    currentPage: 0,
    includeOriginalMedicare: false,
    loading: true,
    oldZip: state.zipcode,
    otherPlansAvailable: 0,
    pageCount: 0,
    plans: [],
    plansToCompareErrorTimer: 0,
    plansToCompareExceeded: false,
    showOriginalMedicareDrawer: false,
    showSwitchPlansHelp: false,
    total_results: 0,

    availableCategories: undefined,
    initialCallMade: false,
  } as SearchResultsState);

  const updatePageQuery = (page: number): void => {
    const query = new URI(props.location.search);
    query.setSearch("page", (page + 1).toString());
    props.history.push(`${props.location.pathname}?${query.query()}`);
  };

  const {
    appState,
    appState: { searchResultsFilters },
    resetToFirstPageOfResults,
    clearPlansToCompare,
    updatePageState,
    defaultSort,
    ...SRHelper
  } = useSearchResultsHelper(
    state,
    appDispatch,
    pageState,
    setPageState,
    updatePageQuery
  );

  // if the user has the old filter structure, clear out the filters
  if (!searchResultsFilters.planCoveragesFilter) {
    appDispatch({
      type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
      payload: defaultState.searchResultsFilters,
    });
  }

  const isMA = isMedicareAdvantageType(appState.planType || PlanType.MAPD);

  // Maintain a SkipNav ref to assign initial page focus
  const skipNavRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    skipNavRef.current?.focus();
  }, [skipNavRef]);

  // fetch the current and next year plan cards in case pharmacies or drugs changed
  useEffect(() => {
    fetchCurrentPlan(
      state.beneficiary,
      state.pharmacies,
      state.lis || LowIncomeSubsidyStatus.LIS_NO_HELP,
      appDispatch,
      state.nextYearCoverage,
      state.futureLis || LowIncomeSubsidyStatus.LIS_NO_HELP
    );
  }, []);

  useEffect(() => {
    if (pageState.includeOriginalMedicare) {
      window.scrollTo(0, 0);
    }
  }, [pageState.includeOriginalMedicare]);

  // If the year changes, we want to clear filters and reset the page to 1
  useEffect(() => {
    if (pageState.initialCallMade) {
      if (doFiltersExist(searchResultsFilters)) {
        appDispatch({
          type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
          payload: defaultState.searchResultsFilters,
        });
        resetToFirstPageOfResults();
      }
      SRHelper.updateCompareList(1);
    }
  }, [appState.year]);

  /**
   * Whenever one of the state properties listed below changes, get an updated list of plans
   */
  useEffect(() => {
    const { page: urlPageNumber } = parseSearchParams(
      new URI(props.location.search).search(true)
    );

    SRHelper.updateCompareList(Number(urlPageNumber));
  }, [
    appState.fips,
    appState.planType,
    appState.snpFilters,
    appState.zipcode,
    searchResultsFilters.planCoveragesFilter,
    searchResultsFilters.insuranceCarrierFilter,
    searchResultsFilters.starRatingFilter,
    searchResultsFilters.drugCoverageFilter,
    searchResultsFilters.planCategoriesFilter,
    searchResultsFilters.sortBy,
  ]);

  /* Update opposite plans (the count) when fips/planType changes */
  useEffect(() => {
    const { fips, planType, year } = appState;
    if (fips && planType && year) {
      SRHelper.requestOppositePlans();
    }
  }, [appState.fips, appState.zipcode, appState.planType, appState.year]);

  const t = useTranslate();

  const planLabel = appState.planType
    ? t(PlanTypeLabels[appState.planType.toUpperCase()])
    : "";

  let heading = `${pageState.total_results} ${planLabel} ${t(
    "search_results_page.plans_available"
  )}`;

  heading = useYearInTitle(heading, state.year);

  const sortPlansByTarget = "total-plan-results";

  return (
    <>
      <AppPage
        headerExtension={
          /* eslint-disable-next-line */
          <div tabIndex={0} ref={skipNavRef}>
            <SkipNav
              href={`#${sortPlansByTarget}`}
              onClick={(e): void => {
                e.preventDefault();
                setIgnoreHeaderFocus(true);
                document.getElementById(sortPlansByTarget)?.focus();
              }}
            >
              {t("skip_to_content")}
            </SkipNav>
            <SearchResultsPageHeader
              pageState={pageState}
              updatePageState={updatePageState}
              {...props}
            />
          </div>
        }
      >
        <ActionsBar
          to={getBackToDrugsAndPharmaciesUrl(appState.prescriptions.length > 0)}
          printEventAction="Find Plans - Available Plans"
          withPrint
        >
          <FormattedMessage id="search_results_page.back_to_drugs_and_pharmacies" />
        </ActionsBar>

        <PlanFiltersContainer
          heading={
            <FocusableHeader
              className="e2e-results-plan-type PlanFilters__resultsHeader"
              text={heading}
              ignoreFocus={ignoreHeaderFocus}
              srOnly
            />
          }
          setIgnoreHeaderFocus={setIgnoreHeaderFocus}
          resetToFirstPageOfResults={resetToFirstPageOfResults}
          loadingPlans={pageState.loading}
          numPlans={pageState.plans.length}
          availableCategories={pageState.availableCategories}
          swapPlanType={() => {
            const oppositePlanType = getOppositePlanType(
              state.planType || PlanType.MAPD
            );

            const switchLink = `/search-results?plan_type=${oppositePlanType.toLowerCase()}&fips=${
              state.fips
            }`;

            clearPlansToCompare();
            updatePageState({ currentPage: 0 });

            props.history.push(switchLink);
            appDispatch({
              type: ActionType.UPDATE_PLANTYPE,
              payload: oppositePlanType,
            });
          }}
        />
        {appState.year &&
          appState.year === appState.currentCoverage?.contract_year && (
            <CoveragePlanCard
              useCurrentCoverage={true}
              pageState={pageState}
              {...props}
            />
          )}

        {appState.year &&
          appState.year === appState?.nextYearCoverage?.contract_year && (
            <CoveragePlanCard
              useCurrentCoverage={false}
              pageState={pageState}
              {...props}
            />
          )}

        <div className="ds-l-row e2e-search-results-page ds-u-margin-top--3 ds-u-md-margin-top--2">
          <div className="ds-l-col">
            <div className="SearchResults__totalAndSortContainer">
              <div className="SearchResults__totalResults">
                <span
                  tabIndex={-1}
                  id="total-plan-results"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <FormattedMessage
                    id="search_results_page.total_plans"
                    tagName="span"
                    values={{
                      current: pageState.plans.length,
                      pluralPlanType: isMA
                        ? t("search_results_page.escape_hatch.medicare_plans")
                        : t("search_results_page.escape_hatch.drug_plans"),
                      total: pageState.total_results,
                    }}
                  />
                </span>
              </div>
              <SortSelector
                id="sorted-list"
                onSortChanged={() => {
                  setIgnoreHeaderFocus(true);
                  resetToFirstPageOfResults();

                  setTimeout(() => {
                    document.querySelector("#sorted-list")?.scrollIntoView();
                  }, 500);
                }}
                defaultSort={defaultSort}
              />
            </div>

            <div className="ds-u-lg-display--none">
              <span role="status" aria-live="polite" aria-atomic="true">
                <FormattedMessage
                  id="search_results_page.total_plans"
                  tagName="span"
                  values={{
                    current: pageState.plans.length,
                    pluralPlanType: isMA
                      ? t("search_results_page.escape_hatch.medicare_plans")
                      : t("search_results_page.escape_hatch.drug_plans"),
                    total: pageState.total_results,
                  }}
                />
              </span>
            </div>

            <ol className="e2e-search-results-list ResultsList ds-c-list ds-c-list--bare">
              {pageState.includeOriginalMedicare && (
                <OriginalMedicarePlanCard />
              )}

              {pageState.plans.map((plan: SearchResultPlan) => (
                <li
                  key={plan.id}
                  aria-labelledby={`${plan.id}-heading`}
                  className="ds-u-margin-bottom--4 e2e-plan-card"
                >
                  <PlanCard
                    plan={plan}
                    handleAddToCompare={SRHelper.handleAddToCompareClick}
                    handleRemoveFromCompare={
                      SRHelper.handleRemovePlanFromCompare
                    }
                    isCurrentPlan={isCurrentPlan(
                      appState.currentCoverage,
                      plan
                    )}
                    {...props}
                  />
                </li>
              ))}
            </ol>

            <div className="SearchResults__footer">
              <OriginalMedicareHelpDrawer
                updatePageState={updatePageState}
                pageState={pageState}
                {...props}
              />

              <div className="ds-u-margin-top--2 mct-u-width--full">
                <Pagination
                  currentPage={pageState.currentPage}
                  totalPages={pageState.pageCount}
                  totalResults={pageState.total_results}
                  onPageSelect={(page): void => {
                    // istanbul ignore next: tested elsewhere
                    SRHelper.requestPlans(page, true);
                  }}
                />
              </div>
            </div>

            <div className="ds-l-col ds-u-display--flex ds-u-justify-content--center ds-u-md-justify-content--end mct-hide-print">
              <span className="ds-u-font-size--sm ds-u-margin-y--2 ds-u-lg-margin-x--6">
                <FormattedMessage
                  id="search_results_page.total_plans"
                  tagName="span"
                  values={{
                    current: pageState.plans.length,
                    pluralPlanType: isMA
                      ? t("search_results_page.escape_hatch.medicare_plans")
                      : t("search_results_page.escape_hatch.drug_plans"),
                    total: pageState.total_results,
                  }}
                />
              </span>
            </div>
          </div>
        </div>
      </AppPage>
      {appState.compareList.length > 0 &&
        !SRHelper.listYearMismatch &&
        appState.fips && (
          <PlanCompareFooter
            list={appState.compareList}
            onRemovePlanFromComparison={SRHelper.handleRemovePlanFromCompare}
            plansExceededError={pageState.plansToCompareExceeded}
            fips={appState.fips}
            pageLoading={pageState.loading}
          />
        )}
    </>
  );
};
